import translations from "@/config/translations";
import useFetch from "@/hooks/useFetch";
import useLangStore from "@/hooks/useLangStore";
import { useEffect, useRef, useState } from "react";
import "./styles.css";

// ── Types ─────────────────────────────────────────────────────────────────────

export interface VerifyOtpPopupDetails {
    operationId: string;
    onSuccess?: () => void;
}

interface Props {
    onClose: () => void;
    details?: VerifyOtpPopupDetails;
}

// ── Component ─────────────────────────────────────────────────────────────────

export default function VerifyOtpPopup({ onClose, details }: Props) {
    const { lang } = useLangStore();
    const t = translations[lang ?? "ar"];
    const [code, setCode] = useState("");
    const [success, setSuccess] = useState(false);

    // ── Verify OTP ──────────────────────────────────────────────────────────────
    const { loading: verifying, status: verifyStatus, error: verifyError, invoke: verifyOtp } = useFetch("auth/verify-otp", { method: "PUT" }, false);

    const wasVerifying = useRef(false);
    useEffect(() => {
        if (verifying) {
            wasVerifying.current = true;
        } else if (wasVerifying.current) {
            wasVerifying.current = false;
            if (verifyStatus === 200) {
                setSuccess(true);
            }
        }
    }, [verifying, verifyStatus]);

    const handleSuccessClose = () => {
        details?.onSuccess?.();
        onClose();
    };

    const handleSubmit = (e: React.FormEvent) => {
        e.preventDefault();
        if (!code.trim() || verifying || !details?.operationId) return;

        verifyOtp({
            data: {
                code: code.trim(),
                operationId: details.operationId,
            },
        });
    };

    const handleCodeChange = (e: React.ChangeEvent<HTMLInputElement>) => {
        const value = e.target.value.replace(/[^0-9]/g, "").slice(0, 4);
        setCode(value);
    };

    // ── Render ─────────────────────────────────────────────────────────────────

    return (
        <div className="verify-otp-popup">
            <div className="vop-header">
                <h2 className="vop-title">{t.verifyOtp.title}</h2>
                <button type="button" className="vop-close" onClick={onClose} aria-label="Close">
                    ×
                </button>
            </div>

            {success ? (
                <div className="vop-success">
                    <div className="vop-success-icon">
                        <img src="/icons/check-icon.svg" alt="" />
                    </div>
                    <p className="vop-success-message">{t.verifyOtp.successMessage}</p>
                    <button type="button" className="vop-btn vop-btn--verify" onClick={handleSuccessClose}>
                        {t.confirmPopup.confirm}
                    </button>
                </div>
            ) : (
                <form className="vop-form" onSubmit={handleSubmit}>
                    <p className="vop-description">{t.verifyOtp.description}</p>

                    <div className="vop-input-wrap">
                        <input
                            type="text"
                            className="vop-input"
                            value={code}
                            onChange={handleCodeChange}
                            placeholder="0000"
                            maxLength={4}
                            autoFocus
                            disabled={verifying}
                        />
                    </div>

                    {verifyError && <p className="vop-error">{verifyError}</p>}

                    <div className="vop-footer">
                        <button type="button" className="vop-btn vop-btn--cancel" onClick={onClose} disabled={verifying}>
                            {t.verifyOtp.cancel}
                        </button>
                        <button type="submit" className="vop-btn vop-btn--verify" disabled={code.length !== 4 || verifying}>
                            {verifying ? t.verifyOtp.verifying : t.verifyOtp.verify}
                        </button>
                    </div>
                </form>
            )}
        </div>
    );
}
